import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardHeader, CardTitle, Input, Select } from '@monigo/ui';
import { callFunction } from '../../lib/api/client.js';
import { qk } from '../../lib/api/keys.js';
import { LoadingScreen } from '../../components/Status/LoadingScreen.js';
import { ErrorState } from '../../components/Status/ErrorState.js';
import { EmptyState } from '../../components/Status/EmptyState.js';

interface FeeRow {
  id: string;
  from_currency: string;
  to_currency: string;
  min_amount: number;
  max_amount: number | null;
  fee_fixed: number;
  fee_percent: number;
  is_active: boolean;
}

const SEND = ['EUR', 'USD', 'CAD'] as const;

export function FeesManagerPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const feesQ = useQuery({
    queryKey: qk.fees,
    queryFn: () => callFunction<{ items: FeeRow[] }>('admin-fees-crud'),
  });
  const create = useMutation({
    mutationFn: (input: Omit<FeeRow, 'id' | 'is_active'>) =>
      callFunction('admin-fees-crud', { body: { ...input, is_active: true } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.fees }),
  });
  const disable = useMutation({
    mutationFn: (id: string) =>
      callFunction('admin-fees-crud', { method: 'DELETE', query: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.fees }),
  });

  const [from, setFrom] = useState<(typeof SEND)[number]>('EUR');
  const [min, setMin] = useState('0');
  const [max, setMax] = useState('');
  const [fixed, setFixed] = useState('0');
  const [pct, setPct] = useState('0');

  if (feesQ.isLoading) return <LoadingScreen />;
  if (feesQ.isError) return <ErrorState onRetry={() => feesQ.refetch()} />;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('fees.addCta')}</CardTitle>
        </CardHeader>
        <form
          className="grid grid-cols-2 gap-2 md:grid-cols-6"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate({
              from_currency: from,
              to_currency: 'XAF',
              min_amount: Number(min),
              max_amount: max === '' ? null : Number(max),
              fee_fixed: Number(fixed),
              fee_percent: Number(pct),
            });
          }}
        >
          <Select
            value={from}
            onChange={(v) => setFrom(v as (typeof SEND)[number])}
            options={SEND.map((c) => ({ value: c, label: `${c}→XAF` }))}
          />
          <Input
            type="number"
            placeholder={t('fees.min')}
            value={min}
            onChange={(e) => setMin(e.target.value)}
          />
          <Input
            type="number"
            placeholder={t('fees.max')}
            value={max}
            onChange={(e) => setMax(e.target.value)}
          />
          <Input
            type="number"
            step="0.01"
            placeholder={t('fees.fixed')}
            value={fixed}
            onChange={(e) => setFixed(e.target.value)}
          />
          <Input
            type="number"
            step="0.0001"
            placeholder={t('fees.percent')}
            value={pct}
            onChange={(e) => setPct(e.target.value)}
          />
          <Button type="submit" isLoading={create.isPending}>
            {t('common.save')}
          </Button>
        </form>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('fees.title')}</CardTitle>
        </CardHeader>
        {feesQ.data?.items.length === 0 ? (
          <EmptyState title={t('fees.empty')} />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-text-muted">
                <th className="px-2 py-2">Corridor</th>
                <th className="px-2 py-2">{t('fees.min')}</th>
                <th className="px-2 py-2">{t('fees.max')}</th>
                <th className="px-2 py-2">{t('fees.fixed')}</th>
                <th className="px-2 py-2">{t('fees.percent')}</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {feesQ.data?.items.map((f) => (
                <tr key={f.id} className="border-t border-border">
                  <td className="px-2 py-2">
                    {f.from_currency}→{f.to_currency}
                  </td>
                  <td className="px-2 py-2 font-mono">{f.min_amount}</td>
                  <td className="px-2 py-2 font-mono">{f.max_amount ?? '∞'}</td>
                  <td className="px-2 py-2 font-mono">{f.fee_fixed}</td>
                  <td className="px-2 py-2 font-mono">{(f.fee_percent * 100).toFixed(2)}%</td>
                  <td className="px-2 py-2 text-right">
                    {f.is_active ? (
                      <Button variant="ghost" size="sm" onClick={() => disable.mutate(f.id)}>
                        {t('common.delete')}
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
